'use strict';
import defaults from 'lodash/defaults';
import isFunction from 'lodash/isFunction';
import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';
import isUndefined from 'lodash/isUndefined';
import 'rxjs/add/observable/empty';
import 'rxjs/add/observable/of';
import 'rxjs/add/observable/timer';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/mergeMap';
import 'rxjs/add/operator/publishReplay';
import 'rxjs/add/operator/share';
import {Observable} from 'rxjs/Observable';
import {buildParentChannelSyncNotifier} from '../../auth/utils/redux-lists';
import {SCOPE_DESTROY} from '../../config/constants/view-events';


export const SYNC_PATH = '_sync-list';
export const SYNC_MODEL_PATH = '_sync';

const MAX_ERROR_COUNT = 100;

export function epicErrorHandler(logger, name) {
  let errorCount = 0;
  /**
   * @param {*} err
   * @param {Observable<T>} source$
   * @return {Observable<T>}
   * @template T
   */
  return (err, source$) => {
    logger.error({err}, name);
    return errorCount < MAX_ERROR_COUNT
      ? Observable.timer(++errorCount * 2 + 100)
        .take(1)
        .mergeMap(() => source$)
      : Observable.empty();
  };
}

export function errorHandler(logger, name, mapTo, maxAttempts = 20, multiplier = 5000) {
  let errorCount = 0;
  return (err, source$) => {
    logger.error({err}, `${name}${isObject(err) ? ' err=' + err.message : ''}`);
    return errorCount < maxAttempts
      ? Observable.timer(++errorCount * multiplier)
        .take(1)
        .mergeMap(() => source$)
      : getErrorHandlerFallback(mapTo);
  };
}

function getErrorHandlerFallback(err, mapTo) {
  let result;
  if (isFunction(mapTo)) {
    result = mapTo(err);
  } else {
    result = mapTo;
  }
  return isUndefined(result) ? Observable.empty() : Observable.of(result);
}

/**
 *
 * @param $scope
 * @param expr
 * @param watchDeep
 * @return {Observable}
 */
export function observeOnScope($scope, expr, watchDeep) {
  return new Observable(observer => {
    const offEvent = $scope.$watch(
      expr,
      (newValue, oldValue) => {
        observer.next({oldValue: oldValue, newValue: newValue});
      },
      watchDeep,
    );

    const offDestroy = $scope.$on('$destroy', function () {
      observer.complete();
    });

    // return the unsubscribe function
    return function () {
      offEvent();
      offDestroy();
    };
  })
    .publishReplay(1)
    .refCount();
}

/**
 *
 * @param $scope
 * @param expr
 * @param watchDeep
 * @return {Observable}
 */
export function observeNewOnScope($scope, expr, watchDeep) {
  return observeOnScope($scope, expr, watchDeep)
    .map(data => data.newValue);
}

/**
 *
 * @param $attrs
 * @param expr
 * @return {Observable}
 */
export function observeOnAttributes($attrs, expr) {
  return new Observable(observer =>
    $attrs.$observe(
      expr,
      newValue => {
        observer.next(newValue);
      }
    )
  )
    .publishReplay(1)
    .refCount();
}

/**
 *
 * @param $scope
 * @param eventName
 * @return {Observable}
 */
export function eventToObservable($scope, eventName) {
  return new Observable(observer => {
    const offEvent = $scope.$on(eventName, function eventToObservableListener() {
      const len = arguments.length;
      const args = new Array(len);
      for (let i = 0; i < len; i++) {
        args[i] = arguments[i];
      }

      observer.next(args.length === 1 ? args[0] : args);
    });

    const offDestroy = $scope.$on('$destroy', function () {
      observer.complete();
    });

    // return the unsubscribe function
    return function () {
      offEvent();
      offDestroy();
    };
  }).share();
}

/**
 * Creates a scope destruction-sensitive timeout
 * User will need to use $scope.$evalAsync if appropriate
 * @param $scope
 * @param delay
 * @return {*|Observable.<T>}
 */
export function timeoutOnScope($scope, delay) {
  return Observable.timer(delay)
    .take(1)
    .takeUntil(eventToObservable($scope, SCOPE_DESTROY));
}

/**
 * Read a file as a data URL
 * @param file
 * @param {Object} [options]
 * @return {Observable<FileData>}
 */
export function readAsDataURL(file, {useProgress = false} = {}) {
  return new Observable(observer => {
    let fileReader;

    try {
      const fileData = {
        name: file.name,
        type: file.type,
        size: file.size,
        url: null,
        progress: null,
      };
      fileReader = new FileReader();

      if (useProgress) {
        fileReader.onprogress = event => {
          observer.next({
            ...fileData,
            progress: event.lengthComputable && isNumber(event.total) ? 100 * event.loaded / event.total : null,
          });
        };
      }

      fileReader.onload = event => {
        observer.next({
          ...fileData,
          url: event.target.result,
        });
        observer.complete();
      };

      fileReader.onerror = e => observer.error(e);

      fileReader.readAsDataURL(file);
    } catch (e) {
      observer.error(e);
    }

    return () => fileReader && fileReader.abort();
  })
    .share();
}

/**
 * @typedef {Object} FileData
 * @property {String} name
 * @property {String} type
 * @property {Number} size
 * @property {String} url
 * @property {Number} [progress]
 */

/**
 * Always return false.
 * Use in epics as .filter(noActionsEmitted) to prevent any actions from being emitted.
 * Mainly used when an epic only has side effects.
 * @returns {boolean}
 */
export function noActionsEmitted() {
  return false;
}

const BASE_SYNC_OPTIONS = {
  pollInterval: 10000,
  pollCount: 6,
};

/**
 * Long poll after each sync notification event.
 * @param {String} parentPath
 * @param {String} childPath
 * @param {Object} [o]
 * @param {Number} [o.pollInterval] Delay between requests in milliseconds.
 * @param {Number} [o.pollCount] Total polling events to attempt
 * @returns {function(*=, *=): Observable<any>}
 */
export function buildPollingSyncNotifier(parentPath, childPath, o = {}) {
  const options = {};
  defaults(options, o, BASE_SYNC_OPTIONS);
  const buildParentNotifier = buildParentChannelSyncNotifier(parentPath, childPath, options.message);
  return (parentId, store) => {
    const parentNotifier$ = buildParentNotifier(parentId, store);

    return parentNotifier$
      .switchMap(() =>
        buildPolling$(options.pollInterval, options.pollCount)
      );
  };
}

/**
 * Build a long polling observable.
 * @param pollInterval
 * @param pollCount
 * @returns {*}
 */
export function buildPolling$(pollInterval, pollCount) {
  return Observable
    .merge(
      // Emit the notification
      Observable.of(null),
      // Then long poll for 60 seconds after the update, unless we get another channel update
      Observable
        .interval(pollInterval)
        .take(pollCount),
    );
}

export function buildInit$(props$) {
  return props$
    .map(props => props.init)
    .filter(init => !!init)
    .take(1);
}



// WEBPACK FOOTER //
// ./src/js/core-utilities/rx-utils/index.js